import React from 'react';
import { AlertTriangle, Sparkles, X } from 'lucide-react';
import { useBilling } from '../BillingContext';
import { useLocation } from 'wouter';

/**
 * Shown in the dashboard header for trial, past_due, and cancelled states.
 * Gracefully hidden when loading or when teacher has a clean active subscription.
 */
export function TrialBanner() {
  const { billing, trialDaysLeft } = useBilling();
  const [, setLocation] = useLocation();

  if (billing.status === 'loading' || billing.status === 'active' || billing.status === 'expired') {
    return null;
  }

  if (billing.status === 'no_subscription') {
    return (
      <div className="w-full px-4 py-2.5 flex items-center justify-between gap-3 text-sm bg-primary/5 border-b border-primary/10 text-primary">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles size={15} className="flex-shrink-0" />
          <span className="font-medium truncate">
            Start your free 7-day trial — no charge until after the trial.
          </span>
        </div>
        <button
          onClick={() => setLocation('/upgrade')}
          className="flex-shrink-0 px-3 py-1 rounded-lg text-xs font-semibold bg-primary text-white hover:bg-primary/90 transition-all"
        >
          Start free trial
        </button>
      </div>
    );
  }

  if (billing.status === 'trial') {
    const urgent = (trialDaysLeft ?? 8) <= 3;
    return (
      <div className={`w-full px-4 py-2.5 flex items-center justify-between gap-3 text-sm
        ${urgent
          ? 'bg-amber-50 border-b border-amber-200 text-amber-900'
          : 'bg-primary/5 border-b border-primary/10 text-primary'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {urgent
            ? <AlertTriangle size={15} className="flex-shrink-0 text-amber-600" />
            : <Sparkles size={15} className="flex-shrink-0" />
          }
          <span className="font-medium truncate">
            {trialDaysLeft === 0
              ? 'Your trial ends today.'
              : trialDaysLeft === 1
                ? '1 day left in your free trial.'
                : `${trialDaysLeft} days left in your free trial.`
            }
          </span>
          {billing.trialEndDate && (
            <span className="text-xs opacity-70 hidden sm:inline">
              · Expires {new Date(billing.trialEndDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
            </span>
          )}
        </div>
        <button
          onClick={() => setLocation('/upgrade')}
          className={`flex-shrink-0 px-3 py-1 rounded-lg text-xs font-semibold transition-all
            ${urgent
              ? 'bg-amber-600 text-white hover:bg-amber-700'
              : 'bg-primary text-white hover:bg-primary/90'
            }`}
        >
          Upgrade
        </button>
      </div>
    );
  }

  if (billing.status === 'past_due') {
    return (
      <div className="w-full px-4 py-2.5 flex items-center justify-between gap-3 text-sm bg-red-50 border-b border-red-200 text-red-900">
        <div className="flex items-center gap-2">
          <AlertTriangle size={15} className="flex-shrink-0 text-red-600" />
          <span className="font-medium">Payment failed — please update your payment method to keep access.</span>
        </div>
        <button
          onClick={() => setLocation('/account')}
          className="flex-shrink-0 px-3 py-1 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition-all"
        >
          Fix payment
        </button>
      </div>
    );
  }

  if (billing.status === 'cancelled') {
    const accessUntil = billing.currentPeriodEnd
      ? new Date(billing.currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
      : null;
    return (
      <div className="w-full px-4 py-2.5 flex items-center justify-between gap-3 text-sm bg-slate-50 border-b border-slate-200 text-slate-700">
        <div className="flex items-center gap-2">
          <span className="font-medium">
            Subscription cancelled.{accessUntil ? ` Full access until ${accessUntil}.` : ''}
          </span>
        </div>
        <button
          onClick={() => setLocation('/account')}
          className="flex-shrink-0 px-3 py-1 rounded-lg text-xs font-semibold bg-slate-700 text-white hover:bg-slate-800 transition-all"
        >
          Reactivate
        </button>
      </div>
    );
  }

  return null;
}
